import React, { useCallback, useMemo, useState } from "react";
import DashboardModal from "../components/DashboardModal";
import DashboardSelect from "../components/DashboardSelect";
import { createPropertyFinancing } from "../services/ownerPatrimonyService";
import { createEmptyFinancingFormValues } from "../utils/buildFinancingFormValues";
import { KenloPropertyOption } from "../utils/getKenloPropertyOptions";
import {
  FinancingFormValues,
  buildFinancingPayload,
  validateFinancingForm,
} from "../utils/validateFinancingForm";
import FinancingFormFields from "./FinancingFormFields";

interface RegisterFinancingModalProps {
  options: KenloPropertyOption[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function RegisterFinancingModal({
  options,
  onClose,
  onSuccess,
}: RegisterFinancingModalProps) {
  const [values, setValues] = useState<FinancingFormValues>(() =>
    createEmptyFinancingFormValues(options[0]?.kenloPropertyId ?? "")
  );
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectOptions = useMemo(
    () =>
      options.map((option) => ({
        value: option.kenloPropertyId,
        label: option.label,
      })),
    [options]
  );

  const handleChange = useCallback(
    <K extends keyof FinancingFormValues>(field: K, value: FinancingFormValues[K]) => {
      setValues((previous) => ({ ...previous, [field]: value }));
    },
    []
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const validationError = validateFinancingForm(values);

    if (validationError) {
      setError(validationError);
      return;
    }

    const payload = buildFinancingPayload(values);

    if (!payload) {
      setError("Revise os campos do financiamento.");
      return;
    }

    setIsSubmitting(true);

    try {
      await createPropertyFinancing(payload);
      onSuccess();
      onClose();
    } catch {
      setError("Não foi possível cadastrar o financiamento. Verifique se já existe registro.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardModal title="Cadastrar financiamento" onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <DashboardSelect
          label="Imóvel"
          value={values.kenloPropertyId}
          options={selectOptions}
          onChange={(kenloPropertyId) => handleChange("kenloPropertyId", kenloPropertyId)}
          placeholder="Selecione um imóvel"
        />

        <FinancingFormFields values={values} onChange={handleChange} />

        {error && <p className="text-sm text-[#f85149]">{error}</p>}

        <button
          type="submit"
          disabled={isSubmitting || options.length === 0}
          className="rounded-full bg-[#6ea8ff] px-4 py-2.5 text-sm font-bold text-[#0d1117] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? "Salvando..." : "Salvar financiamento"}
        </button>
      </form>
    </DashboardModal>
  );
}
